import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaUsers,
  FaUserCheck,
  FaUserTimes,
  FaUserClock,
  FaChartLine,
  FaSync,
  FaBell
} from "react-icons/fa";
import { getTodayOverview } from "../../services/dashboardService";
import "../teacher/TeacherLayer.css";

const AttendanceDashboardLayer = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const res = await getTodayOverview();
      if (res?.status && res?.data) {
        setOverview(res.data);
      }
    } catch (error) {
      console.error("Error loading today overview", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  const tiles = overview?.dashboard_tiles || {
    total_students: 0,
    present_students: 0,
    absent_students: 0,
    late_arrivals: 0,
    attendance_percentage: "0%"
  };

  const recentActivities = overview?.recent_activity || [];

  return (
    <div className="school-wrapper">
      <div className="school-card smooth-card">
        <div className="admin-page-shell">
          
          {/* HEADER */}
          <div className="admin-page-header list-header d-flex flex-column flex-sm-row justify-content-between align-items-start align-items-sm-center gap-3">
            <div>
              <h5 className="admin-page-title mb-1">Attendance Overview</h5>
              <p className="text-muted m-0" style={{ fontSize: "14px" }}>
                Real-time Attendance summary and statistics for today {overview?.date ? `(${overview.date})` : ""}
              </p>
            </div>
            <button 
              className="btn btn-light border d-flex align-items-center justify-content-center p-2 rounded-circle" 
              onClick={fetchOverview} 
              style={{ width: '40px', height: '40px' }} 
              title="Refresh Stats"
            >
              <FaSync className="text-secondary" />
            </button>
          </div>

          {/* DASHBOARD STAT TILES */}
          <div className="unit-count-scope mt-4">
            <style>{`
              .unit-count-scope .stat-card {
                transition: transform 0.2s, box-shadow 0.2s;
                border-radius: 12px;
                border: 1px solid #e3e6f0;
                background: #fff;
              }
              .unit-count-scope .stat-card:hover {
                transform: translateY(-5px);
                box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.1) !important;
                border-color: #6a3cff;
              }
              .unit-count-scope .icon-wrapper {
                width: 48px;
                height: 48px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
              }
              .unit-count-scope .stat-label {
                color: #858796;
                font-size: 0.8rem;
                text-transform: uppercase;
                font-weight: 700;
                margin-bottom: 4px;
              }
              .unit-count-scope .stat-value {
                font-size: 1.5rem;
                font-weight: 800;
                color: #5a5c69;
              }
            `}</style>

            {loading ? (
              <div className="text-center text-muted py-4">
                <div className="spinner-border text-primary spinner-border-sm me-2" role="status"></div>
                Loading statistics...
              </div>
            ) : (
              <div className="row row-cols-xl-5 row-cols-lg-4 row-cols-md-3 row-cols-sm-2 row-cols-1 gy-4">
                
                <div className="col">
                  <div className="card stat-card shadow-sm h-100 border-start border-4 border-primary">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="stat-label text-primary">Total Students</p>
                          <h6 className="stat-value mb-0">{tiles.total_students}</h6>
                        </div>
                        <div className="icon-wrapper" style={{ backgroundColor: "#4e73df20" }}>
                          <FaUsers style={{ color: "#4e73df" }} className="fs-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="card stat-card shadow-sm h-100 border-start border-4 border-success">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="stat-label text-success">Present Students</p>
                          <h6 className="stat-value mb-0">{tiles.present_students}</h6>
                        </div>
                        <div className="icon-wrapper" style={{ backgroundColor: "#1cc88a20" }}>
                          <FaUserCheck style={{ color: "#1cc88a" }} className="fs-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="card stat-card shadow-sm h-100 border-start border-4 border-danger">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="stat-label text-danger">Absent Students</p>
                          <h6 className="stat-value mb-0">{tiles.absent_students}</h6>
                        </div>
                        <div className="icon-wrapper" style={{ backgroundColor: "#e74a3b20" }}>
                          <FaUserTimes style={{ color: "#e74a3b" }} className="fs-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="card stat-card shadow-sm h-100 border-start border-4 border-warning">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="stat-label text-warning">Late Arrivals</p>
                          <h6 className="stat-value mb-0">{tiles.late_arrivals}</h6>
                        </div>
                        <div className="icon-wrapper" style={{ backgroundColor: "#f6c23e20" }}>
                          <FaUserClock style={{ color: "#f6c23e" }} className="fs-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col">
                  <div className="card stat-card shadow-sm h-100 border-start border-4 border-info">
                    <div className="card-body p-3">
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <p className="stat-label text-info">Percentage</p>
                          <h6 className="stat-value mb-0">{tiles.attendance_percentage}</h6>
                        </div>
                        <div className="icon-wrapper" style={{ backgroundColor: "#36b9cc20" }}>
                          <FaChartLine style={{ color: "#36b9cc" }} className="fs-3" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

              </div>
            )}
          </div>

          <div className="row mt-5">
            
            {/* QUICK ACTIONS LINKS */}
            <div className="col-12 col-xl-6 mb-4">
              <div className="card shadow-sm border-0 rounded-3 h-100">
                <div className="card-header bg-white py-3">
                  <h6 className="m-0 fw-bold text-primary">Quick Navigation</h6>
                </div>
                <div className="card-body d-flex align-items-center">
                  <div className="d-flex gap-3 flex-wrap justify-content-start w-100">
                    <Link to="/attendance/classwise" className="btn btn-outline-primary rounded-pill px-4 py-2 fw-semibold">
                      Class-wise Monitoring
                    </Link>
                    {/* <Link to="/attendance/reports" className="btn btn-outline-success rounded-pill px-4 py-2 fw-semibold">
                      Historical Reports
                    </Link> */}
                  </div>
                </div>
              </div>
            </div>

            {/* RECENT ATTENDANCE ACTIVITY */}
            <div className="col-12 col-xl-6 mb-4">
              <div className="card shadow-sm border-0 rounded-3 h-100">
                <div className="card-header bg-white py-3 d-flex align-items-center gap-2">
                  <FaBell className="text-warning" />
                  <h6 className="m-0 fw-bold text-primary">Recent Activity Feed</h6>
                </div>
                <div className="card-body p-0" style={{ maxHeight: "250px", overflowY: "auto" }}>
                  {loading ? (
                    <div className="text-center py-4 text-muted small">Loading activity logs...</div>
                  ) : recentActivities.length === 0 ? (
                    <div className="text-center py-5 text-muted small">No attendance records logged today.</div>
                  ) : (
                    <ul className="list-group list-group-flush">
                      {recentActivities.map((act) => (
                        <li key={act.id} className="list-group-item d-flex align-items-center justify-content-between py-2 px-3 gap-2">
                          <div className="text-truncate">
                            <strong className="text-dark d-block text-truncate" style={{ fontSize: "13.5px" }}>{act.student_name}</strong>
                            <div className="text-muted small text-truncate" style={{ fontSize: "11px" }}>
                              Roll No: {act.roll_number} | Punch In: {act.punch_in || "-"}
                            </div>
                          </div>
                          <span className={`badge rounded-pill px-2.5 py-1 text-nowrap ${
                            act.status === "Present" ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25" :
                            act.status === "Absent" ? "bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25" :
                            "bg-warning bg-opacity-10 text-warning border border-warning border-opacity-25"
                          }`}>
                            {act.status} ({act.attendance_type})
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default AttendanceDashboardLayer;
